import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { CalendarHeart, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

const STYLES = {
  ink: "btn-ink",
  light: "btn-light",
  outline: "btn-outline",
  nav: "",
  header:
    "inline-flex items-center gap-2 px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] bg-olime-ink text-olime-bg hover:bg-olime-stone hover:text-olime-ink transition-colors duration-400",
  headerLight:
    "inline-flex items-center gap-2 px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] border border-white/70 text-white hover:bg-white hover:text-olime-ink transition-colors duration-400",
  footer:
    "inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors duration-300 link-underline",
};

export const BookConsultation = ({ variant = "ink", className = "", testId = "book-consultation-btn", showIcon = true, label }) => {
  const { c } = useLang();
  const { config } = useSiteConfig();
  const url = (config.calendly_url || "").trim();
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const close = useCallback(() => {
    setOpen(false);
    setLoaded(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close]);

  if (!url) return null;

  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const embedUrl = `${url}${url.includes("?") ? "&" : "?"}hide_gdpr_banner=1&embed_domain=${encodeURIComponent(host)}&embed_type=Inline`;

  const openModal = (e) => {
    e.preventDefault();
    setLoaded(false);
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        data-testid={testId}
        onClick={openModal}
        className={`${variant in STYLES ? STYLES[variant] : STYLES.ink} ${className}`}
      >
        {showIcon && <CalendarHeart size={variant === "footer" ? 14 : 15} strokeWidth={1.5} />}
        {label || c.cta.discuss}
      </button>

      {open && createPortal(
        <div
          data-testid="calendly-modal"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[999] flex items-center justify-center p-0 sm:p-6"
        >
          <div
            data-testid="calendly-backdrop"
            onClick={close}
            className="absolute inset-0 bg-olime-ink/70 backdrop-blur-sm"
            style={{ animation: "olimeFadeIn 0.3s ease" }}
          />
          <div
            className="relative z-10 flex h-full w-full flex-col overflow-hidden bg-white shadow-2xl sm:h-[86vh] sm:max-w-[460px]"
            style={{ animation: "olimeModalIn 0.35s cubic-bezier(0.22, 1, 0.36, 1)" }}
          >
            <button
              type="button"
              data-testid="calendly-close"
              onClick={close}
              aria-label="Fermer"
              className="absolute right-3 top-3 z-20 bg-white/85 p-2 text-olime-ink/70 transition-colors duration-300 hover:bg-white hover:text-olime-ink"
            >
              <X size={20} strokeWidth={1.5} />
            </button>

            {!loaded && (
              <div
                data-testid="calendly-loading"
                className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-white"
              >
                <span className="h-8 w-8 animate-spin rounded-full border-2 border-olime-line border-t-olime-ink" />
                <span className="text-[10px] uppercase tracking-[0.25em] text-olime-muted">
                  {label || c.cta.book}…
                </span>
              </div>
            )}

            <iframe
              title="Calendly"
              data-testid="calendly-iframe"
              src={embedUrl}
              className="h-full w-full border-0"
              onLoad={() => setLoaded(true)}
            />
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
